import { FEATURE_ICON_PATHS } from "../features/contracts";
import { uiUrl } from "./portraits";
import "./styles/paintedIcons.css";

/**
 * The painted icons for the meta-game features (FINISH_PLAN WP12 R8): the WP0 names in features/contracts.ts plus the
 * ones the later packages asked for (the skins brush, the Guide book, the achievement medals, the gem packs and the
 * event track's nodes). Rendered in the kit's look (tools/art/icons: small props, studio light, the ink outline) and
 * shipped at @1x/@2x/@3x under art/interface/icons/.
 */
export const PAINTED_ICONS = {
  ...FEATURE_ICON_PATHS,
  skins: "icons/skins.png",
  guide: "icons/guide.png",
  achievementBronze: "icons/achievement_bronze.png",
  achievementSilver: "icons/achievement_silver.png",
  achievementGold: "icons/achievement_gold.png",
  gemPack1: "icons/gem_pack_1.png",
  gemPack2: "icons/gem_pack_2.png",
  gemPack3: "icons/gem_pack_3.png",
  gemPack4: "icons/gem_pack_4.png",
  eventNodeLocked: "icons/event_node_locked.png",
  eventNodeReady: "icons/event_node_ready.png",
  eventNodeClaimed: "icons/event_node_claimed.png",
  // m7 UI pass: the More sheet's feature rows are all painted (the Almanac's tome, the Settings cog).
  almanac: "icons/almanac.png",
  settings: "icons/settings.png",
  // M22 gear v2 (tools/art/icons3d/forge.py): Forge Essence and the Fusion Sigils (Common … Legendary).
  essence: "icons/essence.png",
  sigilCommon: "icons/sigil_common.png",
  sigilUncommon: "icons/sigil_uncommon.png",
  sigilRare: "icons/sigil_rare.png",
  sigilEpic: "icons/sigil_epic.png",
  sigilLegendary: "icons/sigil_legendary.png",
  // mp2 (tools/art/icons3d/features.py): Daily Road's dawn sun over a cobbled road, beside the Events row's ember.
  dailyRoad: "icons/daily_road.png",
  // MP-21 event art (tools/art/icons3d/events.py): each weekly kind and dated event's own picture (events.json `art`);
  // Proving Grounds and Ashen Night keep the ember brazier (eventEmber).
  eventSiege: "icons/event_siege.png",
  eventSwift: "icons/event_swift.png",
  eventBuilders: "icons/event_builders.png",
  eventHunters: "icons/event_hunters.png",
  eventFrostfall: "icons/event_frostfall.png",
  eventGreenwake: "icons/event_greenwake.png",
  eventSunfire: "icons/event_sunfire.png",
  eventHarvest: "icons/event_harvest.png",
  // Round 4 (tools/art/icons3d/modes.py): the event modes' own pictures — Boss Week's horned war-helm on crossed
  // great-axes, Race Week's winged hourglass, the Odyssey's three-armed waymark — used by their weeks (events.json `art`),
  // their panels, end screens and run rows.
  eventBoss: "icons/event_boss.png",
  eventRace: "icons/event_race.png",
  eventOdyssey: "icons/event_odyssey.png",
  // Round 4 (modes.py): the Hall of Records' honours plaque (MP-27) and a challenge code's sealed scroll on crossed
  // swords (MP-28).
  records: "icons/records.png",
  challenge: "icons/challenge.png",
  // Round 4 (modes.py): Endless's amethyst infinity loop on its plinth and the Iron challenges' iron-bound shield with
  // its steel star (M21): their Events rows, sheet heads and end screens (the in-battle tag and the small inline
  // Iron-star badges keep their line glyphs, ui/modeGlyphs.ts).
  endless: "icons/endless.png",
  iron: "icons/iron.png",
  // MP-06 pre-battle supplies (tools/art/icons3d/supplies.py): the battle card's wells and the Shop's Supplies tiles.
  supplyBanner: "icons/supply_banner.png",
  supplyMap: "icons/supply_map.png",
  supplyWagon: "icons/supply_wagon.png",
  supplyHorn: "icons/supply_horn.png",
  // MP-23 (tools/art/icons3d/currency.py res_trophies): Trophies, the Trophy Hall's earned currency — a round crimson
  // buckler with a gilt laurel on crossed swords (not the achievements' cup, not the Hall of Records' plaque).
  trophies: "icons/res_trophies.png",
} as const;
export type PaintedIcon = keyof typeof PAINTED_ICONS;

/** The @1x files are this many px square; @2x and @3x are two and three times it. */
export const PAINTED_ICON_1X_PX = 64;
/** The scale a lone URL (a pill's icon, a fly-to-pill sprite) uses: sharp up to 42 CSS px on a 3× phone. */
const URL_SCALE = 2;

export function isPaintedIcon(name: string): name is PaintedIcon {
  return Object.prototype.hasOwnProperty.call(PAINTED_ICONS, name);
}

function scaled(path: string, k: 1 | 2 | 3): string { return k === 1 ? path : path.replace(/\.png$/, `@${k}x.png`); }

/** One URL for the icon (for `<img src>`-only consumers such as kit.ts pillHtml and fly.ts sprites). */
export function paintedIconUrl(name: PaintedIcon, k: 1 | 2 | 3 = URL_SCALE): string { return uiUrl(scaled(PAINTED_ICONS[name], k)); }

/**
 * The icon as an `<img>` `cssPx` square with its @2x/@3x in `srcset`. A single-file build inlines images as data URLs,
 * whose commas a `srcset` cannot hold, so there the @3x alone is the source.
 */
export function paintedIconHtml(name: PaintedIcon, cssPx: number, cls = ""): string {
  const x1 = paintedIconUrl(name, 1), x3 = paintedIconUrl(name, 3);
  const src = x1.startsWith("data:") ? `src="${x3}"` : `src="${x1}" srcset="${paintedIconUrl(name, 2)} 2x, ${x3} 3x"`;
  return `<img class="pico${cls ? ` ${cls}` : ""}" decoding="sync" ${src} width="${cssPx}" height="${cssPx}" alt="">`;
}

/** The medal for a mastery trim (achievements.json mastery ids: bronze, silver, gold); unknown ids get bronze. */
export function medalIcon(trim: string): PaintedIcon {
  return trim === "gold" ? "achievementGold" : trim === "silver" ? "achievementSilver" : "achievementBronze";
}
